import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Handshake, Pencil, Plus, RefreshCcw, StopCircle, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { DealEditorDialog } from "@/components/deal-editor-dialog";
import { apiErrorMessage } from "@/lib/api";
import { formatRelativeTime } from "@/lib/utils";
import { confirmDealTest, deleteDeal, listDeals, repriceDeal, updateDeal } from "@/api/cpa";
import { listCampaignsPaged } from "@/api/campaigns";
import { DEAL_STATUS_VARIANT, dealSummary } from "@/lib/deals";
import type { DealDto, DealSide, UpsertDealRequest } from "@/types/cpa";

/**
 * The "Deals" tab body on advertiser + affiliate detail pages: one card per
 * deal (not a bare table) with the plain-English summary sentence front and
 * centre, plus lifecycle actions. `side` fixes which money direction the
 * cards and the editor speak in.
 */
export function DealsPanel({
    side,
    counterpartyId,
}: {
    side: DealSide;
    counterpartyId: number;
}): React.ReactNode {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [editor, setEditor] = React.useState<{ open: boolean; deal: DealDto | null }>({
        open: false,
        deal: null,
    });

    const dealsQuery = useQuery({
        queryKey: ["deals", side, counterpartyId],
        queryFn: () =>
            listDeals(side === "advertiser" ? { advertiserId: counterpartyId } : { affiliateId: counterpartyId }),
    });
    // Campaign names for the scope badge — one cached fetch shared app-wide.
    const campaignsQuery = useQuery({
        queryKey: ["campaigns", "deal-scope"],
        queryFn: () => listCampaignsPaged({ size: 200, sort: "name,asc" }),
        staleTime: 60_000,
    });
    const campaignName = React.useCallback(
        (id: number | null): string | null => {
            if (id == null) return null;
            const c = campaignsQuery.data?.content.find((row) => row.id === id);
            return c ? c.name : `Campaign #${id}`;
        },
        [campaignsQuery.data],
    );

    const refresh = (): void => {
        queryClient.invalidateQueries({ queryKey: ["deals"] });
        queryClient.invalidateQueries({ queryKey: ["campaign-commercials"] });
    };

    const confirmTestMut = useMutation({
        mutationFn: (id: number) => confirmDealTest(id),
        onSuccess: () => {
            toast.success("Test phase confirmed — full-price accrual resumed");
            refresh();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't confirm the test phase")),
    });
    const endMut = useMutation({
        mutationFn: ({ id, req }: { id: number; req: UpsertDealRequest }) => updateDeal(id, req),
        onSuccess: () => {
            toast.success("Deal ended");
            refresh();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't end the deal")),
    });
    const deleteMut = useMutation({
        mutationFn: (id: number) => deleteDeal(id),
        onSuccess: () => {
            toast.success("Deal deleted");
            refresh();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't delete the deal")),
    });
    const repriceMut = useMutation({
        mutationFn: (id: number) => repriceDeal(id),
        onSuccess: (repriced) => {
            toast.success(
                repriced === 0
                    ? "Nothing to re-price — the ledger already matches this deal's terms"
                    : `Re-priced ${repriced.toLocaleString("en-US")} ledger ${repriced === 1 ? "row" : "rows"}`,
            );
            queryClient.invalidateQueries({ queryKey: ["finance"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Re-pricing failed")),
    });

    const deals = dealsQuery.data ?? [];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm font-medium">Deals</div>
                    <p className="text-xs text-muted-foreground">
                        {side === "advertiser"
                            ? "Money in — what this advertiser pays us per action."
                            : "Money out — what we pay this affiliate per action."}
                    </p>
                </div>
                <Button size="sm" onClick={() => setEditor({ open: true, deal: null })} data-testid="deal-new-button">
                    <Plus className="h-3.5 w-3.5" /> New deal
                </Button>
            </div>

            {dealsQuery.isLoading ? (
                <div className="flex justify-center py-8">
                    <Spinner />
                </div>
            ) : deals.length === 0 ? (
                <EmptyState
                    icon={<Handshake className="h-6 w-6" />}
                    title="No deals yet"
                    description={
                        side === "advertiser"
                            ? "Add the first agreement to start pricing this advertiser's traffic."
                            : "Add the first agreement to start accruing this affiliate's payouts."
                    }
                    action={
                        <Button variant="outline" onClick={() => setEditor({ open: true, deal: null })}>
                            <Plus className="h-3.5 w-3.5" /> New deal
                        </Button>
                    }
                />
            ) : (
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {deals.map((deal) => (
                        <DealCard
                            key={deal.id}
                            deal={deal}
                            campaignName={campaignName(deal.campaignId)}
                            onEdit={() => setEditor({ open: true, deal })}
                            onConfirmTest={() =>
                                confirm.show({
                                    title: "Confirm the test passed?",
                                    description: `The held first ${deal.testCapActions ?? 0} actions get released and future actions accrue at full price. This can't be undone.`,
                                    confirmLabel: "Confirm test passed",
                                    onConfirm: async () => {
                                        await confirmTestMut.mutateAsync(deal.id);
                                    },
                                })
                            }
                            onEnd={() =>
                                confirm.show({
                                    title: "End this deal?",
                                    description:
                                        "New actions stop being priced by it. Everything already in the ledger stays untouched.",
                                    confirmLabel: "End deal",
                                    onConfirm: async () => {
                                        await endMut.mutateAsync({ id: deal.id, req: endRequest(deal) });
                                    },
                                })
                            }
                            onDelete={() =>
                                confirm.show({
                                    title: "Delete this deal?",
                                    description:
                                        "Only possible while nothing has been priced by it yet — once the ledger references it, end the deal instead.",
                                    destructive: true,
                                    confirmLabel: "Delete deal",
                                    onConfirm: async () => {
                                        await deleteMut.mutateAsync(deal.id);
                                    },
                                })
                            }
                            onReprice={() =>
                                confirm.show({
                                    title: "Re-price this deal's ledger?",
                                    description:
                                        "Use after changing the terms (cap raised, test confirmed, amount corrected). Changed ledger rows are superseded with corrected ones — history is never edited in place.",
                                    confirmLabel: "Re-price",
                                    onConfirm: async () => {
                                        await repriceMut.mutateAsync(deal.id);
                                    },
                                })
                            }
                        />
                    ))}
                </div>
            )}

            <DealEditorDialog
                open={editor.open}
                onOpenChange={(open) => setEditor((s) => ({ ...s, open }))}
                side={side}
                counterpartyId={counterpartyId}
                deal={editor.deal}
                onSaved={refresh}
            />
            {confirm.dialog}
        </div>
    );
}

/** Full-field update payload that flips only the status — PATCH validates the whole shape. */
function endRequest(d: DealDto): UpsertDealRequest {
    return {
        campaignId: d.campaignId,
        actionType: d.actionType,
        dealType: d.dealType,
        cpaUsdMicros: d.cpaUsdMicros,
        revshareBps: d.revshareBps,
        qualifyingDepositUsdMicros: d.qualifyingDepositUsdMicros,
        capActions: d.capActions,
        capPeriod: d.capPeriod,
        testCapActions: d.testCapActions,
        status: "ended",
        effectiveFrom: d.effectiveFrom,
        effectiveTo: d.effectiveTo,
        notes: d.notes,
    };
}

function fmtDay(iso: string): string {
    return new Date(iso).toLocaleDateString("en-US", { dateStyle: "medium" });
}

function effectiveWindow(d: DealDto): string {
    if (d.effectiveFrom && d.effectiveTo) return `Effective ${fmtDay(d.effectiveFrom)} – ${fmtDay(d.effectiveTo)}`;
    if (d.effectiveFrom) return `Effective from ${fmtDay(d.effectiveFrom)}`;
    if (d.effectiveTo) return `Effective until ${fmtDay(d.effectiveTo)}`;
    return "No date limits";
}

function DealCard({
    deal,
    campaignName,
    onEdit,
    onConfirmTest,
    onEnd,
    onDelete,
    onReprice,
}: {
    deal: DealDto;
    campaignName: string | null;
    onEdit: () => void;
    onConfirmTest: () => void;
    onEnd: () => void;
    onDelete: () => void;
    onReprice: () => void;
}): React.ReactNode {
    const inTestPhase = deal.testCapActions != null && deal.testConfirmedAt == null;
    return (
        <div className="rounded-md border border-border bg-card p-4" data-testid={`deal-card-${deal.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <Badge variant={DEAL_STATUS_VARIANT[deal.status] ?? "default"}>{deal.status}</Badge>
                <Badge variant="outline">{deal.campaignId == null ? "Global" : campaignName ?? `Campaign #${deal.campaignId}`}</Badge>
                {deal.testCapActions != null &&
                    (deal.testConfirmedAt != null ? (
                        <Badge variant="success" className="inline-flex items-center gap-1">
                            <BadgeCheck className="h-3 w-3" /> Test passed
                        </Badge>
                    ) : (
                        <Badge variant="warning">Test phase — first {deal.testCapActions} held</Badge>
                    ))}
            </div>

            <p className="mt-3 text-sm font-medium" data-testid={`deal-summary-${deal.id}`}>
                {dealSummary(deal)}
            </p>

            <div className="mt-2 text-xs text-muted-foreground">
                {effectiveWindow(deal)} · updated {formatRelativeTime(deal.updatedAt)}
            </div>
            {deal.notes && <div className="mt-1 text-xs text-muted-foreground">{deal.notes}</div>}

            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
                {inTestPhase && (
                    <Button size="sm" onClick={onConfirmTest} data-testid={`deal-confirm-test-${deal.id}`}>
                        <BadgeCheck className="h-3.5 w-3.5" /> Confirm test passed
                    </Button>
                )}
                <Button size="sm" variant="outline" onClick={onEdit} data-testid={`deal-edit-${deal.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={onReprice} title="Re-run pricing after the terms changed">
                    <RefreshCcw className="h-3.5 w-3.5" /> Re-price
                </Button>
                {deal.status !== "ended" && (
                    <Button size="sm" variant="outline" onClick={onEnd} data-testid={`deal-end-${deal.id}`}>
                        <StopCircle className="h-3.5 w-3.5" /> End
                    </Button>
                )}
                <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`deal-delete-${deal.id}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
            </div>
        </div>
    );
}
